/**
 * Admin > Benefits: site-wide default values (what a user gets with no
 * Patreon tier and no local override).
 */
import { useEffect, useState } from "react";
import { Table, Button, Form } from "react-bootstrap";

import {
    BENEFIT_REGISTRY,
    BenefitId,
    BenefitMap,
    BenefitValue,
    getBenefitDefaults,
    getDefaultBenefits,
    isValidBenefitValue,
    saveBenefitDefaults,
} from "../utils/Benefits";
import { showToast } from "../utils/Toasts";
import { LoadingRing } from "../components/LoadingRing";

type DraftMap = Record<BenefitId, BenefitValue | null>;

function toDraft(defaults: BenefitMap): DraftMap {
    return { ...defaults };
}

function draftsEqual(a: DraftMap, b: DraftMap): boolean {
    return BENEFIT_REGISTRY.every((benefit) => a[benefit.id] === b[benefit.id]);
}

export default function AdminBenefits() {
    const empty = toDraft(getDefaultBenefits());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [baseline, setBaseline] = useState<DraftMap>(empty);
    // What the admin is editing. Every benefit always has a value (no unset).
    const [draft, setDraft] = useState<DraftMap>(empty);

    async function load() {
        setLoading(true);
        const defaults = await getBenefitDefaults();
        const next = toDraft(defaults ?? getDefaultBenefits());
        setBaseline(next);
        setDraft({ ...next });
        setLoading(false);
    }

    useEffect(() => {
        load();
    }, []);

    const dirty = !draftsEqual(draft, baseline);

    function setValue(id: BenefitId, value: BenefitValue | null) {
        setDraft((prev) => ({
            ...prev,
            [id]: value,
        }));
    }

    async function save() {
        for (const benefit of BENEFIT_REGISTRY) {
            if (!isValidBenefitValue(benefit.id, draft[benefit.id])) {
                window.alert(`Invalid value for ${benefit.label}.`);
                return;
            }
        }

        const payload = {} as BenefitMap;
        for (const benefit of BENEFIT_REGISTRY) {
            payload[benefit.id] = draft[benefit.id] as BenefitValue;
        }

        setSaving(true);
        const saved = await saveBenefitDefaults(payload);
        setSaving(false);

        if (!saved) {
            showToast("error", "Unable to save benefit defaults.");
            return;
        }

        const next = toDraft(saved);
        setBaseline(next);
        setDraft({ ...next });
        showToast("success", "Benefit defaults saved.");
    }

    return (
        <>
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <div>
                    <h5 className="mb-1">Default Benefits</h5>
                    <p className="text-muted small mb-0">
                        Use this page to define which benefits every account will have by default.
                        Patreon tier benefits will take precedence over these.  
                        Local overrides will take precedence over both.
                    </p>
                </div>
                {dirty && (
                    <>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={loading || saving}
                            onClick={() => setDraft({ ...baseline })}
                        >
                            Discard
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={loading || saving}
                            onClick={save}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </>
                )}
            </div>

            {loading ? (
                <LoadingRing />
            ) : (
                <div className="table-responsive">
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Table
                            striped
                            hover
                            size="sm"
                            className="align-middle"
                            style={{
                                width: "auto",
                                minWidth: 400,
                                maxWidth: 600,
                                margin: "0 auto",
                            }}
                        >
                            <thead>
                                <tr
                                    style={{
                                        backgroundColor: "var(--bs-primary, #0d6efd)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        letterSpacing: "0.02em",
                                        borderBottom: "3px solid var(--bs-primary-dark, #084298)",
                                    }}
                                >
                                    <th style={{ padding: "0.85rem", fontSize: "1.08rem" }}>Benefit</th>
                                    <th className="text-center" style={{ width: "140px", padding: "0.85rem", fontSize: "1.08rem" }}>Default</th>
                                </tr>
                            </thead>
                            <tbody>
                                {BENEFIT_REGISTRY.map((benefit) => {
                                    const value = draft[benefit.id];
                                    const changed = value !== baseline[benefit.id];
                                    const cellStyle = {
                                        ...(changed
                                            ? { backgroundColor: "rgba(255, 193, 7, 0.15)" }
                                            : {}),
                                        padding: "0.75rem",
                                    };

                                    return (
                                        <tr key={benefit.id}>
                                            <td style={{ padding: "0.75rem" }}>
                                                <div className="fw-bold">{benefit.label}</div>
                                                {benefit.description && (
                                                    <div className="small text-muted">{benefit.description}</div>
                                                )}
                                            </td>
                                            <td className="text-center" style={cellStyle}>
                                                {benefit.valueType === "boolean" ? (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={Boolean(value)}
                                                        disabled={saving}
                                                        onChange={(e) => setValue(benefit.id, e.target.checked)}
                                                        aria-label={benefit.label}
                                                    />
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        min={0}
                                                        step={1}
                                                        value={typeof value === "number" ? value : ""}
                                                        disabled={saving}
                                                        className="text-end mx-auto"
                                                        style={{ maxWidth: "88px" }}
                                                        onChange={(e) => {
                                                            if (e.target.value === "") {
                                                                setValue(benefit.id, null);
                                                                return;
                                                            }
                                                            const next = Number.parseInt(e.target.value, 10);
                                                            setValue(
                                                                benefit.id,
                                                                Number.isFinite(next) ? next : null,
                                                            );
                                                        }}
                                                        aria-label={benefit.label}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                 
                 
                    </div>
             
                </div>
            )}
        </>
    );
}
